import React, { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import EventService from '../../services/EventService';

const MONTH_FILTER_OPTIONS = [
  { label: '3 Months', value: 3 },
  { label: '6 Months', value: 6 },
  { label: '12 Months', value: 12 },
];

const DEFAULT_MONTHS = MONTH_FILTER_OPTIONS[0].value;
const CHART_HEIGHT = 300;
const AXIS_STROKE = '#94a3b8';
const MOCK_CHART_DATA = [
  {
    month: 'Sample',
    eventCount: 1,
    revenue: 1000,
    isMockData: true,
  },
];

const formatRevenue = (value) =>
  new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const formatEventCount = (value) =>
  new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const isMockData = payload.some((entry) => entry?.payload?.isMockData);

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
      <p className="mb-2 text-sm font-medium text-slate-700">{`Month: ${label}`}</p>
      {payload.map((entry) => (
        <p
          key={entry.dataKey}
          className="mb-1 text-sm text-slate-600 last:mb-0"
          style={{ color: entry.color }}
        >
          {`${entry.name}: ${
            entry.name === 'Total Revenue'
              ? formatRevenue(entry.value)
              : formatEventCount(entry.value)
          }`}
        </p>
      ))}
      {isMockData ? (
        <p className="mt-2 text-xs font-medium text-amber-600">
          Mock Data - for illustration
        </p>
      ) : null}
    </div>
  );
};

const GrowthBoard = () => {
  const [selectedMonths, setSelectedMonths] = useState(DEFAULT_MONTHS);
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    const loadRevenueChart = async () => {
      try {
        setIsLoading(true);
        setErrorMessage('');

        const revenueRows = await EventService.getEventRevenueChart(
          selectedMonths,
          { signal: controller.signal },
        );

        setChartData(revenueRows);
      } catch (error) {
        if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
          return;
        }

        console.error('Failed to load event revenue chart:', error);
        setChartData([]);
        setErrorMessage('Unable to load revenue data right now.');
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    void loadRevenueChart();

    return () => {
      controller.abort();
    };
  }, [selectedMonths]);

  const hasChartData = chartData.length > 0;
  const displayData = hasChartData ? chartData : MOCK_CHART_DATA;
  const isShowingMockData = !hasChartData && !errorMessage;

  return (
    <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h6 className="mb-1 fw-bold">Event Revenue Growth</h6>
          <p className="mb-0 text-sm text-slate-500">
            Monthly view of total revenue and event count.
          </p>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-600">
          <span>Range</span>
          <select
            value={selectedMonths}
            onChange={(event) => setSelectedMonths(Number(event.target.value))}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition-colors duration-150 focus:border-sky-400"
            aria-label="Select revenue chart month range"
          >
            {MONTH_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div
        className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3"
        style={{ minHeight: `${CHART_HEIGHT}px` }}
      >
        {isLoading ? (
          <div className="flex h-full min-h-[300px] items-center justify-center text-sm text-slate-500">
            Loading revenue chart...
          </div>
        ) : errorMessage ? (
          <div className="flex h-full min-h-[300px] items-center justify-center text-sm text-rose-500">
            {errorMessage}
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <BarChart
                data={displayData}
                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              >
                <XAxis
                  dataKey="month"
                  tickLine={false}
                  axisLine={{ stroke: AXIS_STROKE }}
                />
                <YAxis
                  yAxisId="count"
                  tickLine={false}
                  axisLine={{ stroke: AXIS_STROKE }}
                  allowDecimals={false}
                  tickFormatter={formatEventCount}
                />
                <YAxis
                  yAxisId="revenue"
                  orientation="right"
                  tickLine={false}
                  axisLine={{ stroke: AXIS_STROKE }}
                  tickFormatter={formatRevenue}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar
                  yAxisId="count"
                  dataKey="eventCount"
                  name="Event Count"
                  fill="#D07C3F"
                  radius={[8, 8, 0, 0]}
                />
                <Bar
                  yAxisId="revenue"
                  dataKey="revenue"
                  name="Total Revenue"
                  fill="#434343"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
            {isShowingMockData ? (
              <p className="mt-3 mb-0 text-xs italic text-amber-700">
                * showing mock data, will get removed once you have some real
                data.
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default GrowthBoard;
